import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  Box,
  FormGroup,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useFormikContext } from "formik";
import { intersection, isArray } from "lodash-es";
import React from "react";
import { Props2 } from "../../../clients/graphqlTypes.js";
import { FormikError } from "../../commons/form-fields/FormikError.js";
import { Attachment } from "../attachments/helpers.js";
import { AllowedValueFormControlLabelWithOverride } from "./AllowedValueFormControlLabelWithOverride.js";
import {
  AllowedValue,
  applyPropertyOverrides,
  getAllowedValues,
  getPropValue,
  PropType,
} from "./properties.js";

export interface AllowedValuesExcludeFormWithOverrideFormValues {
  defaultValue: string | string[] | number | number[] | null | undefined;
  allowedValuesExclude: string[] | number[];
  allowedValuesMedia: { valueAsString: string; media: Attachment | null }[];
}

interface Props {
  propAllowedValues: AllowedValue[];
  propertyType: PropType;
  property: Props2;
}

export const AllowedValuesExcludeFormWithOverride = ({
  propAllowedValues,
  propertyType,
  property,
}: Props) => {
  const { t } = useTranslate(["QuoteItem", "Global"]);
  const formikProps =
    useFormikContext<AllowedValuesExcludeFormWithOverrideFormValues>();

  const onAllowedValueChange = async (
    checked: boolean,
    allowedValue: AllowedValue
  ) => {
    const oldValue = formikProps.values.allowedValuesExclude ?? [];
    const newValue = (
      checked
        ? oldValue.filter(v => v !== allowedValue.value)
        : [
            ...oldValue,
            ...(allowedValue.value !== null ? [allowedValue.value] : []),
          ]
    ) as string[] | number[];
    await formikProps.setFieldValue("allowedValuesExclude", newValue);
    await formikProps.setFieldTouched("defaultValue");

    const newProperty = applyPropertyOverrides(
      property,
      formikProps.values.defaultValue,
      newValue
    );
    const propertyValue = getPropValue(newProperty);
    const allowedValues = getAllowedValues(newProperty).map(av => av.value);

    if (isArray(propertyValue)) {
      const newPropertyValue = intersection(propertyValue, allowedValues);
      if (newPropertyValue.length < propertyValue.length) {
        await formikProps.setFieldValue("defaultValue", newPropertyValue);
      }
    } else {
      if (
        propertyValue !== null &&
        !allowedValues.includes(propertyValue as string | number)
      ) {
        await formikProps.setFieldValue("defaultValue", null);
      }
    }
  };

  const onAllowedValueMediaChange = async (
    valueAsString: string,
    media: Attachment | null | undefined
  ) => {
    let newValue = formikProps.values.allowedValuesMedia.filter(
      v => v.valueAsString !== valueAsString
    );
    if (media !== undefined) {
      newValue = [...newValue, { valueAsString, media }];
    }
    await formikProps.setFieldValue("allowedValuesMedia", newValue);
  };

  if (!propAllowedValues.length) return null;

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          {t("Allowed values", { ns: "QuoteItem" })}
        </Typography>
        <IconButton
          color="primary"
          size="small"
          sx={{ flexGrow: 0, flexShrink: 0 }}
          disabled={!formikProps.values.allowedValuesExclude.length}
          onClick={async () => {
            await formikProps.setFieldValue("allowedValuesExclude", []);
          }}
        >
          <Tooltip title={t("Reset", { ns: "Global" })}>
            <RestartAltIcon />
          </Tooltip>
        </IconButton>
      </Stack>
      <FormGroup>
        {propAllowedValues.map(propAllowedValue => {
          const checked =
            formikProps.values.allowedValuesExclude?.every(
              ave => ave !== propAllowedValue.value
            ) ?? true;
          const valueAsString =
            propAllowedValue.value !== null
              ? propAllowedValue.value.toString()
              : "null";
          const overriddenMedia = formikProps.values.allowedValuesMedia?.find(
            avm => avm.valueAsString === valueAsString
          )?.media;
          return (
            <AllowedValueFormControlLabelWithOverride
              key={`prop-allowed-value-${valueAsString}`}
              checked={checked}
              overriddenMedia={overriddenMedia}
              valueAsString={valueAsString}
              onAllowedValueChange={onAllowedValueChange}
              onAllowedValueMediaChange={onAllowedValueMediaChange}
              propAllowedValue={propAllowedValue}
              propertyType={propertyType}
            />
          );
        })}
      </FormGroup>
      <FormikError formikProps={formikProps} name="allowedValuesExclude" />
    </Box>
  );
};
